



import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { encryptPayload, decryptResponse } from "./../CryptoUtil";

const API = "http://localhost/retailer_api/";

const norm = (value) =>
  String(value ?? "").trim().toUpperCase();

const getName = (item) =>
  item?.name || item?.full_name || "";

function authHeaders() {
  let session = {};

  try {
    session = JSON.parse(
      localStorage.getItem("userSession") || "{}"
    );
  } catch (_) {
    session = {};
  }

  return {
    Accept: "application/json",
    ...(session?.token
      ? { Authorization: `Bearer ${session.token}` }
      : {}),
  };
}

function decrypt(raw) {
  if (raw?.encrypted?.payload !== undefined) {
    return decryptResponse(
      raw.encrypted.payload,
      raw.encrypted.transactionId
    );
  }

  if (raw?.payload !== undefined && raw?.transactionId) {
    return decryptResponse(raw.payload, raw.transactionId);
  }

  return raw;
}

function errorMessage(error) {
  try {
    return (
      decrypt(error.response?.data)?.message ||
      error.message ||
      "Something went wrong."
    );
  } catch (_) {
    return error.message || "Something went wrong.";
  }
}

function dealerLocation(dealer) {
  return [
    dealer?.state,
    dealer?.city,
  ]
    .filter(Boolean)
    .join(" / ");
}

const EMPTY_OPTIONS = {
  zsms: [],
  asms: [],
  sms: [],
};

export default function DealerTransfer() {
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalError, setModalError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [dealer, setDealer] = useState(null);
  const [transferType, setTransferType] = useState("");

  const [options, setOptions] = useState(EMPTY_OPTIONS);

  const [zsmId, setZsmId] = useState("");
  const [asmId, setAsmId] = useState("");
  const [smId, setSmId] = useState("");


const [selectedDealerIds, setSelectedDealerIds] = useState([]);
const [bulkMode, setBulkMode] = useState(false);



  async function apiRequest(payload) {
    const response = await axios.post(
      `${API}dealerTransfer.php`,
      encryptPayload(payload),
      {
        withCredentials: true,
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
        },
        timeout: 25000,
        validateStatus: (status) => status >= 200 && status < 500,
      }
    );

    const result = decrypt(response.data);

    if (result?.success !== true) {
      throw new Error(
        result?.message || "Request failed."
      );
    }

    return result;
  }

  async function fetchDealers() {
    const result = await apiRequest({
      action: "list",
    });

    setDealers(
      Array.isArray(result.dealers)
        ? result.dealers
        : []
    );
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        await fetchDealers();
      } catch (err) {
        if (active) {
          setError(errorMessage(err));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  const filteredDealers = useMemo(() => {
    const query = norm(search);

    if (!query) return [];

    return dealers.filter((item) => {
      const text = [
        item.id,
        item.name,
        item.phone,
          item.business_name,
      
        item.state,
        item.city,
        item.zsm_id,
        item.asm_id,
        item.sm_id,
        item.zsm_name,
        item.asm_name,
        item.sm_name,
      ].join(" ");

      return norm(item.city) === query || norm(text).includes(query);
    });
  }, [dealers, search]);



// const filteredDealers = useMemo(() => {
//   const query = norm(search);

//   if (!query) return [];

//   return dealers.filter((item) => {
//     const text = [
//       item.id,
//       item.name,
//       item.business_name,
//       item.phone,
//       item.mobile,
//       item.state,
//       item.city,
//     ]
//       .filter(Boolean)
//       .join(" ");

//     return norm(text).includes(query);
//   });
// }, [dealers, search]);


  const PAGE_SIZE = 10;
  const pageCount = Math.ceil(filteredDealers.length / PAGE_SIZE);
  const visibleDealers = useMemo(
    () => filteredDealers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredDealers, page]
  );




const visibleIds = visibleDealers.map(item => String(item.id));

const allVisibleSelected =
  visibleIds.length > 0 &&
  visibleIds.every(id => selectedDealerIds.includes(id));

function toggleDealer(id) {
  const dealerId = String(id);

  setSelectedDealerIds(prev =>
    prev.includes(dealerId)
      ? prev.filter(x => x !== dealerId)
      : [...prev, dealerId]
  );
}

function toggleSelectAll() {
  setSelectedDealerIds(prev => {
    if (allVisibleSelected) {
      return prev.filter(id => !visibleIds.includes(id));
    }

    return [...new Set([...prev, ...visibleIds])];
  });
}


  const availableZsms = options.zsms;

  // Default records are real database rows, not generated options.
  const isDefault = (item, type) => Boolean(item) && (
    norm(item.id) === norm(`${type}1000`) ||
    norm(getName(item)) === norm(`Default ${type}`)
  );
  const chosenZsm = options.zsms.find(x => norm(x.id) === norm(zsmId));
  const chosenAsm = options.asms.find(x => norm(x.id) === norm(asmId));
  const chosenSm = options.sms.find(x => norm(x.id) === norm(smId));

  const asmMatchesZsm = (asm, zsm) => !zsm ||
    isDefault(asm, "ASM") || isDefault(zsm, "ZSM") ||
    norm(asm.zsm_id) === norm(zsm.id);
  const smMatchesAsm = (sm, asm) => !asm ||
    isDefault(sm, "SM") || isDefault(asm, "ASM") ||
    norm(sm.asm_id) === norm(asm.id);
  const smMatchesZsm = (sm, zsm) => !zsm ||
    isDefault(sm, "SM") || isDefault(zsm, "ZSM") ||
    !sm.zsm_id || norm(sm.zsm_id) === norm(zsm.id);

  const availableAsms = options.asms.filter(item =>
    asmMatchesZsm(item, chosenZsm)
  );
const availableSms = options.sms;


  const validSelection = Boolean(chosenZsm && chosenAsm && chosenSm &&
    asmMatchesZsm(chosenAsm, chosenZsm) &&
    smMatchesAsm(chosenSm, chosenAsm) &&
    smMatchesZsm(chosenSm, chosenZsm));
  const canSubmit = validSelection;

  async function openTransfer(item, type) {
    setDealer(item);
    setTransferType("sm");

    setZsmId("");
    setAsmId("");
    setSmId("");

    setModalError("");
    setOptions(EMPTY_OPTIONS);

    setModalOpen(true);
    setLoadingOptions(true);

    try {
      const result = await apiRequest({
        action: "options",
        dealer_id: item.id,
        transfer_type: "sm",
      });

      setOptions({
        zsms: result.zsms || [],
        asms: result.asms || [],
        sms: result.sms || [],
      });
    } catch (err) {
      setModalError(errorMessage(err));
    } finally {
      setLoadingOptions(false);
    }
  }

  function closeModal() {
    if (saving) return;

    setModalOpen(false);
    setDealer(null);
    setTransferType("");
    setOptions(EMPTY_OPTIONS);
    setModalError("");
  }

  function changeZsm(value) {
    setZsmId(value);
    setAsmId("");
    setSmId("");
  }

  function changeAsm(value) {
    setAsmId(value);
    setSmId("");
  }

 

function changeSm(value) {
  setSmId(value);
  setModalError("");

  if (!value) return;

  const sm = options.sms.find(
    (item) => norm(item.id) === norm(value)
  );

  if (!sm) return;

  // Default SM ke liye existing selection rakho
  if (isDefault(sm, "SM")) return;

  // SM ka actual mapped ASM
  const actualAsm = options.asms.find(
    (item) => norm(item.id) === norm(sm.asm_id)
  );

  // IMPORTANT:
  // SM ke direct ZSM ko FIRST priority do.
  // ASM ka ZSM sirf fallback hai.
 const actualZsmId = sm.zsm_id || actualAsm?.zsm_id;

  const actualZsm = options.zsms.find(
    (item) => norm(item.id) === norm(actualZsmId)
  );

  if (!actualAsm || !actualZsm) {
    setAsmId("");
    setZsmId("");
    setModalError(
      "SM ki mapped ASM/ZSM available nahi hai."
    );
    return;
  }

  // Exact SM database mapping apply karo
  setAsmId(String(actualAsm.id));
  setZsmId(String(actualZsm.id));
}



  async function confirmTransfer() {
    if (!dealer || !canSubmit || !validSelection || saving) return;

    setSaving(true);
    setModalError("");

    try {
      const result = await apiRequest({
        action: "transfer",
        dealer_id: dealer.id,
        transfer_type: "sm",
        zsm_id: zsmId,
        asm_id: asmId,
        sm_id: smId,
      });

      setSuccess(
        result.message || "Dealer transferred successfully."
      );

      closeModalAfterSuccess();

      await fetchDealers();
    } catch (err) {
      setModalError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function closeModalAfterSuccess() {
    setModalOpen(false);
    setDealer(null);
    setTransferType("");
    setOptions(EMPTY_OPTIONS);
  }

  const modalTitle =
    transferType === "zsm"
      ? "Transfer Dealer"
      : transferType === "asm"
      ? "Transfer Dealer to ASM"
      : "Transfer Dealer to SM";

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-2">Dealer Transfer</h4>

      <p className="text-muted mb-4">
        Search dealers by state, city,
        dealer ID or name.
      </p>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="card mb-4">
       
<div className="card-body">
  <label htmlFor="dealerSearch" className="form-label">
    <strong>Search Dealer</strong>
  </label>

  <input
    id="dealerSearch"
    className="form-control"
    type="search"
    placeholder="Business Name, Phone, State, City, Dealer ID"
    value={search}
    onChange={(e) => {
      setSearch(e.target.value);
      setPage(1);
    }}
  />

  <small className="text-muted d-block mt-2">
    Search by Business Name, Phone Number,
    State, City or Dealer ID.
  </small>
</div>

      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="mb-3">
            Dealer List
            {search.trim()
              ? ` (${filteredDealers.length})`
              : ""}
          </h5>

         
<div className="table-responsive">
  <table className="table table-hover table-bordered align-middle">
    <thead className="thead-light">
      <tr>
        <th>Dealer ID</th>
        <th>Name</th>
        <th>Phone</th>
        <th>Business Name</th>
        <th>Location</th>
        <th>Current ZSM</th>
        <th>Current ASM</th>
        <th>Current SM</th>
        <th>Actions</th>
      </tr>
    </thead>

    <tbody>
      {/* Loading */}
      {loading && (
        <tr>
          <td colSpan={9} className="text-center py-4">
            Loading dealers...
          </td>
        </tr>
      )}

      {/* Dealer Rows */}
      {!loading &&
        visibleDealers.map((item) => (
          <tr key={item.id}>
            {/* Dealer ID */}
            <td>
              <strong>{item.id || "—"}</strong>
            </td>

            {/* Dealer Name */}
            <td>
              {getName(item) || "—"}
            </td>

            {/* Phone Number */}
            <td>
              {item.phone || item.mobile || "—"}
            </td>

            {/* Business Name */}
            <td>
              {item.business_name || "—"}
            </td>

            {/* State / City */}
            <td style={{ minWidth: 170 }}>
              {dealerLocation(item) || "—"}
            </td>

            {/* Current ZSM */}
            <td>
              <strong>
                {item.zsm_name || "Unassigned"}
              </strong>
              <div className="small text-muted">
                {item.zsm_id || "—"}
              </div>
            </td>

            {/* Current ASM */}
            <td>
              <strong>
                {item.asm_name || "Unassigned"}
              </strong>
              <div className="small text-muted">
                {item.asm_id || "—"}
              </div>
            </td>

            {/* Current SM */}
            <td>
              <strong>
                {item.sm_name || "Unassigned"}
              </strong>
              <div className="small text-muted">
                {item.sm_id || "—"}
              </div>
            </td>

            {/* Transfer Action */}
            <td>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => openTransfer(item, "sm")}
              >
                Transfer
              </button>
            </td>
          </tr>
        ))}

      {/* Empty Search */}
      {!loading && !search.trim() && (
        <tr>
          <td
            colSpan={9}
            className="text-center py-4 text-muted"
          >
            Search by Business Name, Phone Number,
            Dealer ID, State or City.
          </td>
        </tr>
      )}

      {/* No Search Results */}
      {!loading &&
        search.trim() &&
        filteredDealers.length === 0 && (
          <tr>
            <td
              colSpan={9}
              className="text-center py-4 text-muted"
            >
              No matching dealers found.
            </td>
          </tr>
        )}
    </tbody>
  </table>
</div>

        </div>
      </div>

      {search.trim() && pageCount > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap">
          <span className="text-muted">
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, filteredDealers.length)}
            {" "}of {filteredDealers.length} dealers
          </span>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <button className="btn btn-outline-secondary btn-sm"
              disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn btn-outline-secondary btn-sm"
              disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>
              Next
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          className="modal d-block"
          role="dialog"
          aria-modal="true"
          aria-label={modalTitle}
          style={{
            background: "rgba(0,0,0,0.55)",
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalTitle}
                </h5>

                <button
                  type="button"
                  className="close"
                  onClick={closeModal}
                  disabled={saving}
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>

              <div className="modal-body">
                {modalError && (
                  <div className="alert alert-danger">
                    {modalError}
                  </div>
                )}

                <div className="alert alert-info">
                  <h6 className="font-weight-bold">
                    Current Dealer Mapping
                  </h6>

                  <div>
                    <strong>Dealer:</strong>{" "}
                    {dealer?.id} — {getName(dealer)}
                  </div>

                  <div>
                    <strong>Location:</strong>{" "}
                    {dealerLocation(dealer)}
                  </div>

                  <hr />

                  <div>
                    <strong>ZSM:</strong>{" "}
                    {dealer?.zsm_id} —{" "}
                    {dealer?.zsm_name || "Default ZSM"}
                  </div>

                  <div>
                    <strong>ASM:</strong>{" "}
                    {dealer?.asm_id} —{" "}
                    {dealer?.asm_name || "Default ASM"}
                  </div>

                  <div>
                    <strong>SM:</strong>{" "}
                    {dealer?.sm_id} —{" "}
                    {dealer?.sm_name || "Default SM"}
                  </div>
                </div>

                {loadingOptions ? (
                  <div className="alert alert-info">
                    Loading eligible managers...
                  </div>
                ) : (
                  <>
                    {(
                      <div className="form-group">
                        <label>
                          Select Destination ZSM *
                        </label>

                        <select
                          className="form-control"
                          value={zsmId}
                          onChange={(e) =>
                            changeZsm(e.target.value)
                          }
                        >
                          <option value="">
                            Select ZSM
                          </option>

                          {availableZsms.map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.id} - {getName(item)}
                            </option>
                          ))}
                        </select>

                        <small className="text-muted">
                          Default ZSM is available for every state (when its DB record is active).
                        </small>
                      </div>
                    )}

                    {(
                      <div className="form-group">
                        <label>
                          Select Destination ASM *
                        </label>

                        <select
                          className="form-control"
                          value={asmId}
                          onChange={(e) =>
                            changeAsm(e.target.value)
                          }
                          disabled={
                            !zsmId
                          }
                        >
                          <option value="">
                            Select ASM
                          </option>

                          {availableAsms.map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.id} - {getName(item)}
                            </option>
                          ))}
                        </select>

                        <small className="text-muted">
                          Default ASM can be combined with any eligible ZSM; regular ASMs keep their database mapping.
                        </small>
                      </div>
                    )}

                    <div className="form-group">
                      <label>
                        Select Destination SM *
                      </label>

                      <select
                        className="form-control"
                        value={smId}
                        onChange={(e) =>
                          changeSm(e.target.value)
                        }
                        disabled={
                          false
                        }
                      >
                        <option value="">
                          Select SM
                        </option>

                        {availableSms.map((item) => (
                          <option
                            key={item.id}
                            value={item.id}
                          >
                            {item.id} - {getName(item)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {validSelection && (
                      <div className="alert alert-success">
                        <strong>
                          New Reporting Hierarchy
                        </strong>

                        <div>
                          ZSM: {zsmId}{isDefault(chosenZsm, "ZSM") ? " (Default)" : ""}
                        </div>

                        <div>
                          ASM: {asmId}{isDefault(chosenAsm, "ASM") ? " (Default)" : ""}
                        </div>

                        <div>
                          SM: {smId}{isDefault(chosenSm, "SM") ? " (Default)" : ""}
                        </div>
                      </div>
                    )}

                    {!options.sms.length && (
                      <div className="alert alert-warning">
                        No eligible SM found for this
                        dealer location. Check saved
                        SM working area, parent ASM
                        and ZSM mappings.
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmTransfer}
                  disabled={
                    saving ||
                    loadingOptions ||
                    !canSubmit || !validSelection
                  }
                >
                  {saving
                    ? "Transferring..."
                    : "Confirm Transfer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
